import { cloneElement, useState, useEffect, MouseEvent } from 'react';
import { H1 } from 'components/01-atoms/Heading';
import { Image } from 'components/01-atoms/Image';
import { Icon } from 'components/01-atoms/Icon';
import { useOutsideLogin } from 'hooks/useOutsideLogin';

export type LearnHeroProps = {
  /** Text for the header */
  headingText: string;
  /** Text for the dek */
  dekText?: string;
  /** Logo to be displayed in the upper right hand corner of the hero */
  logoSource?: string;
  /** Video for the hero background */
  video?: JSX.Element;
  /** Image for the hero background */
  image?: JSX.Element;
  /** Should the video be hidden even if there is one? Used to hide the video for non-O+ users */
  hideVideo?: boolean;
};

export const LearnHero = ({
  headingText,
  dekText = '',
  logoSource,
  image,
  video,
  hideVideo = false
}: LearnHeroProps): JSX.Element => {
  const [showOverlay, setShowOverlay] = useState(true);

  const toggleShowOverlay = () => setShowOverlay((prevOverlay) => !prevOverlay);

  const showVideo = video && !hideVideo;
  const showImage = image && (!video || hideVideo);
  const showFakeVideoButton = video && hideVideo && image;

  const { isLoaded, showJoinNow } = useOutsideLogin();

  const handleOnJoin = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isLoaded && hideVideo) {
      showJoinNow();
    }
  };

  // in case a video is in play (showOverlay is false) and the user logs out, show the overlay again
  useEffect(() => {
    if (hideVideo) {
      setShowOverlay(true);
    }
  }, [hideVideo]);

  return (
    <section className="w-full overflow-hidden bg-network-black narrow:aspect-h-9 narrow:aspect-w-16">
      <div className="relative flex w-full flex-col items-end narrow:flex-row">
        <div className="w-full narrow:absolute narrow:left-0 narrow:top-0">
          {showVideo &&
            cloneElement(
              video as JSX.Element,
              { onPlay: toggleShowOverlay },
              null
            )}
          {showImage && (
            <div className="relative">
              {cloneElement(image as JSX.Element)}
              {showFakeVideoButton && (
                <button
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform cursor-pointer"
                  onClick={handleOnJoin}
                  aria-label="Join Button"
                  type="button"
                  style={{ zIndex: 1 }}
                >
                  <Icon
                    type="playButton"
                    size="xxlarge"
                    color="#fff"
                    classes="opacity-50 hover:opacity-100 "
                  />
                </button>
              )}
            </div>
          )}
        </div>
        {showOverlay && (
          <>
            <div className="absolute h-full w-full bg-network-black bg-opacity-40" />
            <div className="absolute left-5 top-4 narrow:left-12 narrow:top-12">
              {logoSource && (
                <Image
                  alt="brand logo"
                  classes="h-auto w-full"
                  src={logoSource}
                />
              )}
            </div>
          </>
        )}
        <div
          className={`relative h-full w-full bg-transparent text-network-white ${
            !showOverlay ? 'narrow:invisible narrow:h-0' : ''
          }`.trim()}
        >
          <div className="bottom-0 px-5 pb-8 pt-6 narrow:absolute">
            <H1 headingText={headingText} classes="mb-2" />
            <p
              className="u-text--p-std text-network-white narrow:w-1/2 narrow:max-w-40"
              style={{ fontSize: '1em' }}
            >
              {dekText}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
